/**
 * ActionChainOverlay — sticky panel shown while an Action Chain is active.
 *
 * Renders the ordered list of reactable actions and, when the local viewer is
 * the reactor, exposes Pass / Respond controls. "Respond" enables the player
 * to play a legal Reaction-subtype utility from their hand.
 *
 * Collapsible: a small chevron tab on the panel's left edge slides the panel
 * off the right side of the screen. Clicking the tab again brings it back.
 */
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Card } from "@/types/card";
import type { GameState } from "@/types/game";
import { BoardCard } from "./BoardCard";

interface ActionChainOverlayProps {
  game: GameState;
  /** The local player's perspective (1 or 2). Used to decide if Pass/Respond
   *  controls should be shown. */
  viewerNum: 1 | 2;
  cardMap: Map<string, Card>;
  onPass: () => void;
}

export function ActionChainOverlay({
  game,
  viewerNum,
  cardMap,
  onPass,
}: ActionChainOverlayProps) {
  // Default to EXPANDED so the local viewer immediately sees the reaction
  // window when an action chain opens. They may collapse it via the chevron.
  const [collapsed, setCollapsed] = useState(false);
  const chain = game.actionChain;
  const isViewerReactor = chain?.reactor === viewerNum;
  const lastEntryId = chain?.entries.length
    ? chain.entries[chain.entries.length - 1].id
    : null;
  // Auto-expand whenever a new chain entry is pushed OR the local viewer
  // becomes the reactor. This guarantees the panel is visible at the moment
  // a decision is required.
  useEffect(() => {
    if (lastEntryId && isViewerReactor) setCollapsed(false);
  }, [lastEntryId, isViewerReactor]);
  if (!chain || chain.entries.length === 0) return null;
  const reactorName =
    chain.reactor != null ? game.players[chain.reactor].name : null;

  // Phase A: utility-only chains render CENTER-SCREEN so the player can't
  // miss the reaction window. Combat-related chains (legacy attack-play /
  // equip-attack snapshots) keep the original right-sticky position so they
  // don't obscure the combat popup that's already on-screen. Phase B will
  // revisit this once attacks also become deferred stack entries.
  // Phase B: combat entries now live on `deferredEffect.kind === "attack-play"`
  // (the legacy `appliedSnapshot` field is no longer populated when an attack
  // is pushed). Falling back to the legacy field keeps any in-flight pre-Phase-B
  // saves rendering correctly.
  const hasCombatEntry = chain.entries.some((e) => {
    const deferredKind = (e as { deferredEffect?: { kind?: string } }).deferredEffect?.kind;
    if (deferredKind === "attack-play") return true;
    const snap = (e as { appliedSnapshot?: { kind?: string } }).appliedSnapshot;
    return snap?.kind === "attack-play" || snap?.kind === "equip-attack" || snap?.kind === "sig-attack";
  });
  const positionClass = hasCombatEntry
    ? "pointer-events-none fixed right-0 top-20 z-40 flex items-start"
    : "pointer-events-none fixed inset-0 z-40 flex items-center justify-center";

  return (
    <div
      className={positionClass}
      data-testid="action-chain-overlay"
    >
      {/* Collapse / expand tab — always visible, sits on the panel's left edge.
          Arrow points LEFT when expanded (click to collapse), RIGHT when
          collapsed (click to expand back). */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? "Expand Action Chain" : "Collapse Action Chain"}
        className="pointer-events-auto -mr-px flex h-10 w-6 items-center justify-center rounded-l-md border border-r-0 border-amber-400/60 bg-zinc-950/95 text-amber-300 shadow-2xl ring-1 ring-amber-300/30 hover:bg-zinc-900"
        data-testid="button-action-chain-toggle"
      >
        {collapsed ? (
          // Hidden → arrow points LEFT ("pull me out")
          <ChevronLeft className="h-4 w-4" />
        ) : (
          // Visible → arrow points RIGHT ("push me back")
          <ChevronRight className="h-4 w-4" />
        )}
      </button>

      <div
        className={
          hasCombatEntry
            ? `pointer-events-auto w-72 overflow-hidden rounded-l-lg border border-r-0 border-amber-400/60 bg-zinc-950/95 p-3 shadow-2xl ring-1 ring-amber-300/30 transition-transform duration-300 ease-out sm:w-80 ${
                collapsed ? "translate-x-full" : "translate-x-0"
              }`
            : `pointer-events-auto w-80 max-w-[90vw] overflow-hidden rounded-lg border border-amber-400/60 bg-zinc-950/95 p-3 shadow-2xl ring-1 ring-amber-300/30 transition-opacity duration-300 ease-out sm:w-96 ${
                collapsed ? "pointer-events-none opacity-0" : "opacity-100"
              }`
        }
      >
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-wider text-amber-300">
            Action Chain
          </span>
          <span className="text-[9px] uppercase text-zinc-500">
            {chain.entries.length} action{chain.entries.length === 1 ? "" : "s"}
          </span>
        </div>

        {reactorName && chain.reactor != null && (
          <div className="mt-1 text-xs text-zinc-300">
            {isViewerReactor ? (
              <span className="font-semibold text-amber-200">
                You may react.
              </span>
            ) : (
              <span>Waiting on {reactorName} to react…</span>
            )}
          </div>
        )}

        {/* Stacked entries (oldest at top, newest at bottom) */}
        <ol className="mt-3 flex flex-col gap-2">
          {chain.entries.map((entry, i) => {
            const card = entry.cardId ? cardMap.get(entry.cardId) ?? null : null;
            const playerName = game.players[entry.playerNum].name;
            return (
              <li
                key={entry.id}
                className="flex items-start gap-2 rounded border border-zinc-800 bg-zinc-900/70 px-2 py-1.5"
              >
                <span className="shrink-0 rounded bg-amber-500/20 px-1.5 text-[10px] font-bold text-amber-200">
                  {i + 1}
                </span>
                {card && (
                  <div className="shrink-0">
                    <BoardCard card={card} size="prep" hidePills />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[11px] font-semibold text-zinc-100">
                    {playerName}
                  </div>
                  <div className="truncate text-[10px] text-zinc-400">
                    {entry.description}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        {/* Reactor controls */}
        {isViewerReactor && (
          <div className="mt-3 flex flex-col gap-2">
            <p className="text-[10px] text-zinc-400">
              Respond by playing a legal Reaction utility from your hand, or
              pass to close the chain.
            </p>
            <button
              onClick={onPass}
              className="rounded bg-zinc-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-zinc-600"
              data-testid="button-action-chain-pass"
            >
              Pass
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
